// Shared client/server validation for wallet checkout contact details.
// Phase 1 scope: email for every order; a delivery address ONLY for printed books,
// US destinations only. Digital orders must not carry an address at all.

export type WalletProduct = 'digital' | 'printed';

export interface ShippingAddressInput {
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  province: string; // US state code
  zip: string;
  countryCode: string; // ISO-2
  phone: string;
}

export interface ContactInput {
  email: string;
  shippingAddress?: ShippingAddressInput | null;
}

export interface ValidContact {
  email: string;
  shippingAddress: ShippingAddressInput | null;
}

export type ContactErrors = Partial<Record<'email' | keyof ShippingAddressInput | 'form', string>>;

/** Destinations the wallet route can ship printed books to (phase 1). */
export const WALLET_SHIPPING_COUNTRIES = ['US'] as const;

export const US_STATES: ReadonlyArray<[string, string]> = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'],
  ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'],
  ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'],
  ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'],
  ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'],
  ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];
const US_STATE_CODES = new Set(US_STATES.map(([c]) => c));

// Plain addresses only — no quotes / angle brackets / separators (the email reaches Shopify,
// our emails' To: header and the admin UI).
const EMAIL_RE = /^[A-Za-z0-9._%+'-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
const ZIP_RE = /^\d{5}(-\d{4})?$/;
const MAX = 120;

/**
 * Lulu's own shipping_address limits (Print API spec, /print-jobs/ + /print-job-cost-calculations/):
 * name ≤ 35 (first + last joined), street1/street2/city ≤ 30, phone ≤ 20 matching
 * `^\+?[\d\s\-.\/()]{8,20}$`. A printed wallet order must fit them up front — Lulu rejects
 * the print job otherwise, long after the customer has paid.
 */
export const LULU_ADDRESS_LIMITS = { name: 35, street: 30, city: 30, phone: 20 } as const;
const LULU_PHONE_RE = /^\+?[\d\s\-./()]{8,20}$/;
/**
 * What a real name / street / city can contain: letters (any language, incl. accents),
 * digits, spaces and . , ' # & ( ) / -. Anything else (< > " ; { } \ ` etc.) is refused —
 * these values travel to Shopify, Lulu and the printed label, so markup / script / query
 * fragments never get stored. (SQL is parameterized by Prisma regardless.)
 */
const SAFE_TEXT_RE = /^[\p{L}\p{M}0-9 .,'#&()\/-]+$/u;

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/**
 * A real US phone number (NANP): 10 digits, or 11 starting with the country code 1.
 * Area code and exchange can't start with 0 or 1, and N11 service codes (211…911) are not
 * area codes. Returns E.164 (+1XXXXXXXXXX) — the form Shopify and Lulu both take — or null.
 */
export function normalizeUsPhone(v: string): string | null {
  let d = (v || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  if (d.length !== 10) return null;
  const area = d.slice(0, 3);
  const exchange = d.slice(3, 6);
  if (!/^[2-9]\d{2}$/.test(area) || /^[2-9]11$/.test(area)) return null;
  if (!/^[2-9]\d{2}$/.test(exchange) || /^[2-9]11$/.test(exchange)) return null;
  return `+1${d}`;
}

/** "(212) 555-1234" for display, from anything normalizeUsPhone accepts (else unchanged). */
export function formatUsPhone(v: string): string {
  const e164 = normalizeUsPhone(v);
  if (!e164) return v;
  const d = e164.slice(2);
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function cleanPhone(v: string): string {
  return v.replace(/[^\d+]/g, '');
}

export function validateContact(
  product: WalletProduct,
  input: unknown,
): { ok: true; value: ValidContact } | { ok: false; errors: ContactErrors } {
  const errors: ContactErrors = {};
  const raw = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;

  const email = str(raw.email).toLowerCase();
  if (!email || email.length > 254 || !EMAIL_RE.test(email)) errors.email = 'Enter a valid email address';

  const addrRaw = raw.shippingAddress;

  if (product === 'digital') {
    if (addrRaw != null) errors.form = 'Digital orders do not take a delivery address';
    return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value: { email, shippingAddress: null } };
  }

  if (product !== 'printed') return { ok: false, errors: { form: 'Unsupported product' } };

  const a = (addrRaw && typeof addrRaw === 'object' ? addrRaw : {}) as Record<string, unknown>;
  const addr: ShippingAddressInput = {
    firstName: str(a.firstName),
    lastName: str(a.lastName),
    address1: str(a.address1),
    address2: str(a.address2) || undefined,
    city: str(a.city),
    province: str(a.province).toUpperCase(),
    zip: str(a.zip),
    countryCode: str(a.countryCode).toUpperCase(),
    phone: cleanPhone(str(a.phone)),
  };

  if (!addr.firstName) errors.firstName = 'Required';
  if (!addr.lastName) errors.lastName = 'Required';
  if (!addr.address1) errors.address1 = 'Required';
  if (!addr.city) errors.city = 'Required';
  for (const k of ['firstName', 'lastName', 'address1', 'address2', 'city'] as const) {
    if ((addr[k] ?? '').length > MAX) errors[k] = 'Too long';
  }
  for (const k of ['firstName', 'lastName', 'address1', 'address2', 'city'] as const) {
    const v = addr[k];
    if (v && !SAFE_TEXT_RE.test(v)) errors[k] = 'Use letters, numbers and . , # - / only';
  }
  // Lulu's limits (see LULU_ADDRESS_LIMITS) — friendlier than a rejected print job.
  const L = LULU_ADDRESS_LIMITS;
  if (addr.firstName && addr.lastName && `${addr.firstName} ${addr.lastName}`.length > L.name) {
    errors.lastName = `First + last name must be ${L.name} characters or less`;
  }
  if (addr.address1.length > L.street) errors.address1 = `Max ${L.street} characters — abbreviate (St, Ave, Apt)`;
  if ((addr.address2 ?? '').length > L.street) errors.address2 = `Max ${L.street} characters`;
  if (addr.city.length > L.city) errors.city = `Max ${L.city} characters`;
  if (!(WALLET_SHIPPING_COUNTRIES as readonly string[]).includes(addr.countryCode)) {
    errors.countryCode = 'Express pay ships printed books to the US only — use regular checkout for other countries';
  }
  if (!US_STATE_CODES.has(addr.province)) errors.province = 'Choose a state';
  if (!ZIP_RE.test(addr.zip)) errors.zip = 'Enter a 5-digit ZIP code';
  // A real US number (the books ship within the US and the courier calls it), stored as
  // E.164 — which also fits Lulu's phone pattern and 20-char limit.
  const usPhone = normalizeUsPhone(addr.phone);
  if (!usPhone || usPhone.length > L.phone || !LULU_PHONE_RE.test(usPhone)) {
    errors.phone = addr.phone ? 'Enter a valid US phone number, e.g. (212) 555-1234' : 'Enter a phone number for the courier';
  } else {
    addr.phone = usPhone;
  }

  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value: { email, shippingAddress: addr } };
}
